import * as React from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { NavLink, Outlet, useLocation, useNavigate } from "react-router-dom";
import {
  Activity,
  Archive,
  Boxes,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  FileText,
  FlaskConical,
  Gauge,
  History,
  KeyRound,
  LogOut,
  Plug,
  ScrollText,
  Settings2,
  ShieldCheck,
  SlidersHorizontal,
  Users,
  ToggleLeft,
  ShieldAlert,
  TrendingUp,
  Building2,
  LayoutGrid,
  Server,
} from "lucide-react";
import { toast } from "sonner";

import { cn } from "@/lib/utils";
import { formatRelative } from "@/lib/date";
import { Button } from "@/components/ui/button";
import { clearStaleBuildGuard } from "@/components/common/StaleBuild";
import { Separator } from "@/components/ui/separator";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { ThemeToggle } from "./ThemeToggle";
import { HeaderHelpButton } from "@/features/help/HeaderHelpButton";
import { useMe } from "@/components/auth/RequireAuth";
import { authKeys, logout } from "@/lib/api/auth";
import { fetchInventory } from "@/lib/api/inventory";
import { MODULE_GROUP_DEFENDER, MODULE_SECURITY_SCAN } from "@/lib/api/modules";
import { useModules } from "@/lib/modules";
import { fetchRuns, runKeys } from "@/lib/api/runs";
import { defaultInventoryFilter, inventoryKeys } from "@/lib/api/inventory";
import { useMultitenant } from "@/lib/modules";
import { clearTenantScope, useTenantPathFn } from "@/lib/tenant";
import { TenantSwitcher } from "./TenantSwitcher";
import { readCollapsed, writeCollapsed } from "./sidebarState";


interface NavItem {
  to: string;
  label: string;
  icon: React.ComponentType<{ className?: string }>;
  /** Marks the section root so nested routes keep the parent highlighted. */
  end?: boolean;
  /**
   * The optional module this entry belongs to. Absent means the entry is part
   * of the console proper and is always shown.
   */
  module?: string;
  /**
   * A navigation GROUP of modules — the entry is shown while any module in the
   * group is in effect. Used for Defender, where five separately licensed
   * products share one heading.
   */
  moduleGroup?: string;
  /**
   * A PLATFORM destination: it belongs to the deployment, not to a tenant, so
   * its `to` is used verbatim rather than resolved against /t/{slug}. The
   * operators, the audit trail and the platform settings are the same three
   * pages whichever tenant is open.
   */
  platform?: boolean;
}

const PRIMARY_NAV: NavItem[] = [
  { to: "/overview", label: "Overview", icon: Gauge },
  { to: "/inventory", label: "Inventory", icon: Boxes },
  // Belongs to the security scan module. With the module off it is removed
  // rather than disabled: a greyed-out entry advertises a part of the console
  // this deployment has decided it does not have.
  { to: "/posture", label: "Posture", icon: ShieldCheck, module: MODULE_SECURITY_SCAN },
  // One entry for five separately licensed products. They share a heading
  // because that is how an operator thinks of Defender; the page inside says
  // which of the five this tenant actually has. The entry disappears only when
  // ALL five are off or unlicensed — a heading over nothing is worse than no
  // heading.
  { to: "/defender", label: "Defender", icon: ShieldAlert, moduleGroup: MODULE_GROUP_DEFENDER },
  { to: "/changes", label: "Changes", icon: History },
  // The retained runs over time. A read surface for every role, so it sits
  // with the other read surfaces rather than under Reports.
  { to: "/history", label: "History", icon: TrendingUp },
  { to: "/scans", label: "Scans", icon: Activity },
  { to: "/reports", label: "Reports", icon: FileText },
];

const ADMIN_NAV: NavItem[] = [
  { to: "/admin/users", label: "Users", icon: Users, platform: true },
  { to: "/admin/audit", label: "Audit log", icon: ScrollText, platform: true },
  { to: "/admin/platform", label: "Platform", icon: Server, platform: true },
];

/**
 * The navigation of the PLATFORM shell — the pages that are about the
 * deployment rather than about one tenant.
 *
 * It is a different list, not a filtered one: "Overview" on this shell would
 * have to mean the overview of some tenant, and there is no such tenant here.
 */
const PLATFORM_NAV: NavItem[] = [
  { to: "/tenants", label: "Tenants", icon: LayoutGrid, end: true, platform: true },
];

// "Manage tenants" is a multitenant surface: a single-tenant deployment has
// no registry, so the entry is only listed with the module on.
const MANAGE_TENANTS_NAV: NavItem = {
  to: "/tenants/manage",
  label: "Manage tenants",
  icon: Building2,
  platform: true,
};
const PLATFORM_ADMIN_NAV: NavItem[] = ADMIN_NAV;

/**
 * The settings pages, grouped the way the registry groups them.
 *
 * DEFAULT settings change a number this product reports. EXTENDED settings
 * change WHICH APIs it is willing to call, and therefore what the report can
 * contain at all. Somebody enabling one is accepting a dependency, not tuning a
 * threshold, and the two must not look like the same kind of decision — which
 * is exactly what a single long page made them look like.
 *
 * The pages themselves are generated from the API's group/page fields, so this
 * list is the navigation order and nothing else. A key added to the registry
 * appears on its page without touching this file.
 */
const SETTINGS_GROUPS: { label: string; note: string; items: NavItem[] }[] = [
  {
    label: "Default settings",
    note: "Change what this product reports.",
    items: [
      // One page for the tenant's connection: the Entra application, tenant
      // init and its updates, permissions and consent, and capabilities.
      { to: "/admin/settings/tenant-connection", label: "Tenant connection", icon: Plug },
      { to: "/admin/settings/scanning", label: "Scanning", icon: Activity },
      // Scoring and thresholds only tune what the security scan reports, so
      // they go with it.
      {
        to: "/admin/settings/scoring",
        label: "Scoring",
        icon: Gauge,
        module: MODULE_SECURITY_SCAN,
      },
      {
        to: "/admin/settings/thresholds",
        label: "Thresholds",
        icon: SlidersHorizontal,
        module: MODULE_SECURITY_SCAN,
      },
      {
        to: "/admin/settings/emergency-access",
        label: "Emergency access",
        icon: KeyRound,
      },
      { to: "/admin/settings/retention", label: "Retention", icon: Archive },
      { to: "/admin/settings/reporting", label: "Reporting", icon: FileText },
    ],
  },
  {
    label: "Extended settings",
    note: "Change which APIs this product calls.",
    items: [{ to: "/admin/settings/preview", label: "Preview APIs", icon: FlaskConical }],
  },
  {
    label: "Modules",
    note: "Turn whole parts of this console on or off.",
    items: [{ to: "/admin/settings/modules", label: "Modules", icon: ToggleLeft }],
  },
];

/** Every settings route, for the collapsed-sidebar active check. */
const SETTINGS_ROUTES = SETTINGS_GROUPS.flatMap((g) => g.items.map((i) => i.to));

/**
 * Filters navigation by the modules this deployment has enabled.
 *
 * An entry belonging to a disabled module is REMOVED, not disabled. A greyed
 * item advertises a part of the console this deployment has decided it does not
 * have, and invites a click that can only end in a refusal.
 */
function useNavFilter(): (items: readonly NavItem[]) => NavItem[] {
  const { enabled, defender } = useModules();
  // Only one group exists today. A map would be premature: a second group
  // would want its own flag on the hook anyway, since each is a different
  // question about a different set of modules.
  const groupEnabled = (g: string) => (g === MODULE_GROUP_DEFENDER ? defender : true);
  return (items) =>
    items.filter((i) => {
      if (i.module !== undefined && !enabled(i.module)) return false;
      if (i.moduleGroup !== undefined && !groupEnabled(i.moduleGroup)) return false;
      return true;
    });
}

export interface AppShellProps {
  /**
   * Which shell this is.
   *
   * "tenant" is the console proper, mounted under /t/{slug}. "platform" is the
   * deployment's own pages — the tenant list, the operators, the audit trail,
   * the platform settings — which have no tenant at all and must not pretend
   * to: no tenant name in the header, no last-scan indicator, and a navigation
   * that names only what exists at this level.
   */
  variant?: "tenant" | "platform";
}

export function AppShell({ variant = "tenant" }: AppShellProps = {}): React.ReactElement {
  const [collapsed, setCollapsed] = React.useState<boolean>(() => readCollapsed());
  const me = useMe();
  const visible = useNavFilter();
  const { enabled: multitenantOn } = useMultitenant();
  const platform = variant === "platform";
  // A platform page belongs to no tenant, so no request it makes may carry the
  // slug of whichever tenant was open last. Cleared on the way in, during
  // render, before any child has had the chance to fetch anything.
  if (platform) clearTenantScope();

  // The shell mounted, so the current build loaded. Release the one-shot
  // reload guard: without this a tab that recovered from one deploy would
  // refuse to auto-recover from the next one in the same session.
  React.useEffect(() => {
    clearStaleBuildGuard();
  }, []);
  const isAdmin = me.data?.role === "admin";

  const toggleSidebar = (): void => {
    setCollapsed((prev) => {
      writeCollapsed(!prev);
      return !prev;
    });
  };

  return (
    <div className="flex min-h-screen bg-background text-foreground">
      <a
        href="#main-content"
        className="focus-ring sr-only focus:not-sr-only focus:absolute focus:left-3 focus:top-3 focus:z-50 focus:rounded-md focus:bg-popover focus:px-3 focus:py-2 focus:text-sm focus:shadow-md"
        data-testid="skip-link"
      >
        Skip to main content
      </a>

      <aside
        data-testid="sidebar"
        data-collapsed={collapsed ? "true" : "false"}
        className={cn(
          "sticky top-0 flex h-screen shrink-0 flex-col border-r bg-card transition-[width]",
          collapsed ? "w-14" : "w-60",
        )}
      >
        <div
          className={cn(
            "flex h-14 items-center border-b",
            collapsed ? "justify-center px-2" : "justify-between px-3",
          )}
        >
          {collapsed ? null : (
            <span className="text-sm font-semibold tracking-tight">sp-ms365</span>
          )}
          <Button
            variant="ghost"
            size="icon"
            data-testid="sidebar-toggle"
            aria-label={collapsed ? "Expand navigation" : "Collapse navigation"}
            aria-expanded={!collapsed}
            onClick={toggleSidebar}
          >
            {collapsed ? <ChevronRight aria-hidden="true" /> : <ChevronLeft aria-hidden="true" />}
          </Button>
        </div>

        <nav aria-label="Primary" className="flex flex-1 flex-col gap-1 overflow-y-auto p-2">
          {(platform ? PLATFORM_NAV : visible(PRIMARY_NAV)).map((item) => (
            <SidebarLink key={item.to} item={item} collapsed={collapsed} />
          ))}

          {/*
            The Administration group is RENDERED ONLY FOR ADMINS. sp-migrate
            shows its Settings item to every operator and bounces the click to
            /403, which reads as a broken product rather than as a door that
            is not theirs.
          */}
          {isAdmin ? (
            <div className="mt-4 flex flex-col gap-1" data-testid="nav-admin-group">
              <Separator className="my-1" />
              {collapsed ? null : (
                <p className="px-2 pb-1 pt-2 text-[11px] font-semibold uppercase tracking-wide text-ink-3">
                  Administration
                </p>
              )}
              {platform ? null : <SettingsNav collapsed={collapsed} />}
              {(platform
                ? multitenantOn
                  ? [MANAGE_TENANTS_NAV, ...PLATFORM_ADMIN_NAV]
                  : PLATFORM_ADMIN_NAV
                : ADMIN_NAV
              ).map((item) => (
                <SidebarLink key={item.to} item={item} collapsed={collapsed} />
              ))}
            </div>
          ) : null}
        </nav>
      </aside>

      <div className="flex min-w-0 flex-1 flex-col">
        <header className="sticky top-0 z-30 flex h-14 shrink-0 items-center justify-between gap-4 border-b bg-background/95 px-4 backdrop-blur">
          {platform ? <PlatformHeading /> : <TenantHeading />}
          <div className="flex items-center gap-2">
            {platform ? null : <LastScanIndicator />}
            <HeaderHelpButton />
            <ThemeToggle />
            <AccountMenu />
          </div>
        </header>

        <main
          id="main-content"
          tabIndex={-1}
          className="min-w-0 flex-1 p-4 focus-visible:outline-none sm:p-6"
        >
          <Outlet />
        </main>
      </div>
    </div>
  );
}

function SidebarLink({
  item,
  collapsed,
}: {
  item: NavItem;
  collapsed: boolean;
}): React.ReactElement {
  const Icon = item.icon;
  const resolve = useTenantPathFn();
  // The testid derives from the LITERAL `to`, not from the resolved path:
  // `nav-overview` must stay `nav-overview` whichever tenant is open.
  const href = item.platform === true ? item.to : resolve(item.to);
  const link = (
    <NavLink
      to={href}
      end={item.end}
      data-testid={`nav-${item.to.replace(/\//g, "-").replace(/^-/, "")}`}
      className={({ isActive }) =>
        cn(
          "focus-ring flex items-center gap-3 rounded-md px-2 py-2 text-sm transition-colors",
          collapsed ? "justify-center" : "",
          isActive
            ? "bg-brand-soft font-medium text-brand-ink"
            : "text-ink-2 hover:bg-accent hover:text-accent-foreground",
        )
      }
    >
      <Icon className="h-4 w-4 shrink-0" aria-hidden="true" />
      {collapsed ? <span className="sr-only">{item.label}</span> : <span>{item.label}</span>}
    </NavLink>
  );

  if (!collapsed) return link;
  return (
    <Tooltip>
      <TooltipTrigger asChild>{link}</TooltipTrigger>
      <TooltipContent side="right">{item.label}</TooltipContent>
    </Tooltip>
  );
}

/**
 * The tenant this console is looking at.
 *
 * Read from the inventory - the `organization` object is the tenant's own
 * directory record, and every role can read it. When no organization object
 * has been collected the header says so; it never falls back to a placeholder
 * name or to the browser's hostname.
 */
function TenantHeading(): React.ReactElement {
  const { enabled: multitenant } = useMultitenant();
  // With several tenants the header is a CONTROL, not a caption: the operator
  // needs to know which tenant they are in and to move between them, and two
  // widgets saying the same thing would compete.
  if (multitenant) return <TenantSwitcher />;
  return <TenantName />;
}

/**
 * The deployment itself, on the platform shell.
 *
 * It deliberately names no tenant. A platform page belongs to none of them,
 * and borrowing the last-opened tenant's name here would be a caption that is
 * simply untrue.
 */
function PlatformHeading(): React.ReactElement {
  return (
    <div className="flex min-w-0 flex-col" data-testid="platform-name">
      <span className="truncate text-sm font-semibold">Platform</span>
      <span className="truncate text-[11px] text-ink-3">
        Tenants, operators and deployment-wide settings
      </span>
    </div>
  );
}

function TenantName(): React.ReactElement {
  const query = useQuery({
    queryKey: inventoryKeys.list("organization", defaultInventoryFilter, { page: 1, size: 1 }),
    queryFn: () => fetchInventory("organization", defaultInventoryFilter, { page: 1, size: 1 }),
    staleTime: 300_000,
    retry: false,
  });

  const organisation = query.data?.items[0];

  return (
    <div className="flex min-w-0 flex-col" data-testid="tenant-name">
      <span className="truncate text-sm font-semibold">
        {query.isPending ? (
          <span className="text-ink-3">Loading tenant...</span>
        ) : organisation === undefined || organisation.display_name === "" ? (
          <span className="text-state-nc" title="No organization object has been collected yet.">
            Tenant not identified
          </span>
        ) : (
          organisation.display_name
        )}
      </span>
      <span className="truncate text-[11px] text-ink-3">
        Microsoft 365 &amp; Entra ID inventory - read only
      </span>
    </div>
  );
}

/**
 * The last scan.
 *
 * Reads the newest run. There is no invented "healthy" state: a deployment
 * that has never scanned says so, and a failed run says so.
 */
function LastScanIndicator(): React.ReactElement {
  const resolve = useTenantPathFn();
  const query = useQuery({
    queryKey: runKeys.list({ page: 1, size: 1 }),
    queryFn: () => fetchRuns({ page: 1, size: 1 }),
    staleTime: 30_000,
    retry: false,
  });

  const run = query.data?.items[0];

  let body: React.ReactNode;
  if (query.isPending) {
    body = <span className="text-ink-3">Checking scans...</span>;
  } else if (query.isError) {
    body = <span className="text-state-nc">Scan status unavailable</span>;
  } else if (run === undefined) {
    body = <span className="text-state-nc">No scan has run yet</span>;
  } else if (run.active) {
    body = (
      <span className="text-brand-ink">
        Scan running - started {formatRelative(run.started_at)}
      </span>
    );
  } else {
    body = (
      <span className="text-ink-2">
        Last scan {formatRelative(run.finished_at ?? run.created_at)} - {run.status}
      </span>
    );
  }

  return (
    <NavLink
      to={resolve(run === undefined ? "/scans" : `/scans/${run.id}`)}
      data-testid="last-scan"
      className="focus-ring hidden rounded-md px-2 py-1 text-xs hover:bg-accent sm:block"
    >
      {body}
    </NavLink>
  );
}

function AccountMenu(): React.ReactElement {
  const me = useMe();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const signOut = useMutation({
    mutationFn: logout,
    onSuccess: (result) => {
      queryClient.removeQueries({ queryKey: authKeys.me() });
      if (result.end_session_url !== null) {
        window.location.assign(result.end_session_url);
        return;
      }
      void navigate("/login", { replace: true });
    },
    onError: (error: unknown) => {
      toast.error("Sign out failed", {
        description: error instanceof Error ? error.message : String(error),
      });
    },
  });

  const initials = initialsFor(me.data?.name ?? me.data?.email ?? "");

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          data-testid="account-menu"
          aria-label="Account menu"
          className="rounded-full bg-brand-soft text-xs font-semibold text-brand-ink"
        >
          {initials === "" ? <Users aria-hidden="true" /> : initials}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-64">
        <DropdownMenuLabel>
          <span className="block truncate text-sm font-medium text-foreground">
            {me.data?.name === undefined || me.data.name === "" ? "Signed in" : me.data.name}
          </span>
          <span className="block truncate text-xs font-normal text-ink-3">
            {me.data?.email ?? ""}
          </span>
          <span className="mt-1 block text-xs font-normal text-ink-3">
            Role: {me.data?.role ?? "unknown"}
            {me.data?.dev_mode === true ? " (dev mode)" : ""}
          </span>
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        <DropdownMenuItem
          data-testid="sign-out"
          disabled={signOut.isPending}
          onSelect={(event) => {
            event.preventDefault();
            signOut.mutate();
          }}
        >
          <LogOut aria-hidden="true" />
          {signOut.isPending ? "Signing out..." : "Sign out"}
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

function initialsFor(value: string): string {
  const parts = value
    .trim()
    .split(/[\s@.]+/)
    .filter((p) => p !== "");
  if (parts.length === 0) return "";
  const first = parts[0]?.charAt(0) ?? "";
  const second = parts.length > 1 ? (parts[1]?.charAt(0) ?? "") : "";
  return `${first}${second}`.toUpperCase();
}

/**
 * Settings as a collapsible section rather than one long page.
 *
 * It stays open when the current route is a settings page, so navigating
 * between them never collapses the list under the cursor. The open/closed
 * choice is remembered per browser, because an operator who works in settings
 * should not re-open the section on every visit.
 *
 * With the sidebar collapsed to icons there is nowhere to put a submenu, so the
 * item becomes a single link to the first page — a disclosure arrow with no
 * room to disclose into is worse than no arrow.
 */
const SETTINGS_OPEN_KEY = "spm365.sidebar.settingsOpen";

function SettingsNav({ collapsed }: { collapsed: boolean }): React.ReactElement {
  const location = useLocation();
  const visible = useNavFilter();
  const resolve = useTenantPathFn();
  // `includes`, not `startsWith`: the settings pages live under /t/{slug}.
  const onSettings = location.pathname.includes("/admin/settings");

  const [open, setOpen] = React.useState<boolean>(() => {
    if (onSettings) return true;
    try {
      return window.localStorage.getItem(SETTINGS_OPEN_KEY) !== "false";
    } catch {
      return true;
    }
  });

  // Navigating INTO settings opens the section. Navigating away leaves the
  // operator's own choice alone.
  React.useEffect(() => {
    if (onSettings) setOpen(true);
  }, [onSettings]);

  function toggle(): void {
    setOpen((prev) => {
      try {
        window.localStorage.setItem(SETTINGS_OPEN_KEY, String(!prev));
      } catch {
        // A preference that could not be saved is not worth an error.
      }
      return !prev;
    });
  }

  if (collapsed) {
    return (
      <SidebarLink
        item={{ to: SETTINGS_ROUTES[0], label: "Settings", icon: Settings2 }}
        collapsed
      />
    );
  }

  return (
    <div data-testid="settings-nav">
      <button
        type="button"
        onClick={toggle}
        aria-expanded={open}
        aria-controls="settings-subnav"
        data-testid="settings-nav-toggle"
        className={cn(
          "focus-ring flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-sm transition-colors",
          onSettings ? "text-ink" : "text-ink-2 hover:bg-muted hover:text-ink",
        )}
      >
        <Settings2 className="h-4 w-4 shrink-0" aria-hidden="true" />
        <span className="flex-1 text-left">Settings</span>
        <ChevronDown
          className={cn("h-3.5 w-3.5 shrink-0 transition-transform", open ? "" : "-rotate-90")}
          aria-hidden="true"
        />
      </button>

      {open ? (
        <div id="settings-subnav" className="mt-0.5 flex flex-col gap-2 pb-1">
          {SETTINGS_GROUPS.map((group) => (
            <div key={group.label}>
              <p className="px-2 pb-0.5 pl-8 text-[10px] font-semibold uppercase tracking-wide text-ink-3">
                {group.label}
              </p>
              {/*
                The note is not decoration. "Default" and "Extended" mean
                nothing on their own, and the difference — reports a number
                versus calls a different API — is the whole reason the two are
                separated.
              */}
              <p className="px-2 pb-1 pl-8 text-[10px] leading-tight text-ink-3">{group.note}</p>
              {visible(group.items).map((item) => (
                <NavLink
                  key={item.to}
                  to={resolve(item.to)}
                  className={({ isActive }) =>
                    cn(
                      "focus-ring flex items-center gap-2 rounded-md py-1.5 pl-8 pr-2 text-sm transition-colors",
                      isActive
                        ? "bg-primary/10 font-medium text-primary"
                        : "text-ink-2 hover:bg-muted hover:text-ink",
                    )
                  }
                >
                  <item.icon className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
                  <span className="truncate">{item.label}</span>
                </NavLink>
              ))}
            </div>
          ))}
        </div>
      ) : null}
    </div>
  );
}
